// NOTE: createClientId is duplicated from IncomingOrderViewDialog.
// Consider extracting both to a shared util (e.g. src/utils/clientId.js).
import { useEffect, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";


const textFieldSx = {
  mt: 1,
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: "8px",
    bgcolor: "#fff",
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "#fff",
    px: 0.5,
  },
};

/**
 * CreateViewGroupDialog
 *
 * Används för att skapa en ny grupp eller redigera en befintlig.
 *
 * Props:
 *   open          – boolean
 *   onClose       – () => void
 *   onCreateGroup – (group) => void   – anropas vid skapande (group.id är ett klient-id)
 *   onRenameGroup – (id, name) => void – anropas vid redigering
 *   editGroup     – { id, name } | null – om satt körs dialogen i redigeringsläge
 */
const CreateViewGroupDialog = ({ open, onClose, onCreateGroup, onRenameGroup, editGroup, onDeleteGroup }) => {

  const isEditing = !!editGroup?.id;

  const [name, setName] = useState("");

  useEffect(() => {
    if (open) {
      setName(isEditing ? (editGroup.name ?? "") : "");
    }
  }, [open, isEditing, editGroup]);

  const canSubmit = name.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;

    
    if (isEditing) {
      onRenameGroup?.({
        id: editGroup.id,
        name: name.trim(),
      });
    } else {
      onCreateGroup?.({
        id: 0,
        name: name.trim(),
      });
    }
  };

  const handleDelete = () => {
    if (!isEditing) return;

    onDeleteGroup?.(editGroup.id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "12px",
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <FolderRoundedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>
              {isEditing ? "Redigera grupp" : "Skapa grupp"}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 12.5, mt: 0.35 }}>
              {isEditing
                ? "Ändra namn på gruppen."
                : "Skapa en tom grupp där du senare kan samla vyer."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Gruppnamn"
          placeholder="Ex. Plocklista"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={textFieldSx}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {isEditing && (
            <Button
              color="error"
              onClick={handleDelete}
              sx={{ textTransform: "none" }}
            >
              Radera
            </Button>
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Avbryt
    </Button>

          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{ textTransform: "none" }}
          >
            {isEditing ? "Spara" : "Skapa grupp"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CreateViewGroupDialog;